import React, { useEffect, useState } from "react";
import {
  useNavigate,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./dashboard/theme";
import { ReactSession } from "react-client-session";
import { AppProvider } from "@toolpad/core/AppProvider";
import { usePermissions } from "./pages/hooks/usePermissions";
import { NAVIGATION_CONFIG } from "./pages/constants/navigation";
import DashboardLayoutBasic from "./dashboard/DashboardLayoutBasic";
import Login from "./pages/Login";

function App() {
  const [theme, colorMode] = useMode();
  const [loggedIn, setLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, loading } = usePermissions();
  const userRole = ReactSession.get("role");

  ReactSession.setStoreType("localStorage");
  const username = ReactSession.get("username");

  const handleLogin = () => {
    if (username && username !== "") {
      setLoggedIn(true);
      navigate("/");
    } else {
      setLoggedIn(false);
      navigate("/login");
    }
  };

  useEffect(() => {
    handleLogin();
  }, [username]);

  const isItemSelected = (item) => {
    const currentPath = location.pathname.split("/");
    if (item.children) {
      return (
        item.children.some((child) => currentPath.includes(child.segment)) ||
        currentPath.includes(item.segment)
      );
    }
    return currentPath.includes(item.segment);
  };

  const checkItemPermissions = (item) => {
    if (item.kind === "header") return true;
    if (userRole === "admin") return true;
    if (item.adminOnly) return false;

    if (item.children) {
      const hasValidChildren = item.children.some(
        (child) => !child.adminOnly && hasPermission(child.requiredPermission)
      );
      return hasValidChildren;
    }

    return hasPermission(item.requiredPermission);
  };

  const processNavigationItem = (item) => {
    if (item.kind === "header") return item;

    const processedItem = {
      ...item,
      icon: item.icon ? (
        <item.icon
          sx={{
            color: isItemSelected(item)
              ? `${theme.palette.secondary.main} !important`
              : "inherit",
            opacity: 1,
          }}
        />
      ) : null,
      title: (
        <span
          style={{
            color: isItemSelected(item)
              ? theme.palette.secondary.main
              : "inherit",
          }}
        >
          {item.title}
        </span>
      ),
      selected: isItemSelected(item),
    };

    if (item.children) {
      processedItem.children = item.children
        .filter((child) => checkItemPermissions(child))
        .map((child) => ({
          ...processNavigationItem(child),
          icon: child.icon ? (
            <child.icon
              sx={{
                color: isItemSelected(child)
                  ? `${theme.palette.secondary.main} !important`
                  : "inherit",
                opacity: 1,
              }}
            />
          ) : null,
          title: (
            <span
              style={{
                color: isItemSelected(child)
                  ? theme.palette.secondary.main
                  : "inherit",
              }}
            >
              {child.title}
            </span>
          ),
          selected: isItemSelected(child),
        }));
    }

    return processedItem;
  };

  const filteredNavigation = NAVIGATION_CONFIG.filter(checkItemPermissions).map(
    processNavigationItem
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route
            path="/login"
            element={
              !loggedIn ? <Login onLogin={handleLogin} /> : <Navigate to="/" />
            }
          />
          <Route
            path="/*"
            element={
              loggedIn ? (
                <AppProvider
                  navigation={filteredNavigation}
                  router={{
                    pathname: location.pathname,
                    navigate,
                    searchParams: new URLSearchParams(location.search),
                  }}
                  theme={theme}
                >
                  <DashboardLayoutBasic
                    loggedIn={loggedIn}
                    onLogin={handleLogin}
                  />
                </AppProvider>
              ) : (
                <Navigate to="/login" />
              )
            }
          />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
